// src/services/auditLog.js
//
// Client-side audit envelope. Every state-changing API call records a
// before/after snapshot here, which is batched and shipped to the
// `audit_log` table on the Replit server.
//
// Design notes:
//   - Records are queued in-memory and persisted to localStorage on every
//     change. If the user closes the tab before flush, the queue survives.
//   - Async batch (every 5s, plus on `beforeunload`). Audit failures must
//     never block a user write - missing audits are recoverable, blocked
//     saves are not.
//   - On POST failure: the queue stays in localStorage and a banner is
//     surfaced via the bus so the AppShell can render a warning. Per
//     CLAUDE.md, never silently mutate state without a corresponding log.
//   - Sanitization is applied at the LOGGER boundary only (see logger.js).
//     The raw before/after is shipped to the server intact, since the
//     audit_log table is admin-only and field-level sensitivity is enforced
//     at the read path, not the write path.

import { log } from "../utils/logger.js";

// ─── Configuration ────────────────────────────────────────────────────────

const QUEUE_STORAGE_KEY = "ripple_audit_queue_v1";
const FLUSH_INTERVAL_MS = 5000;
const MAX_BATCH_SIZE    = 50;
const MAX_QUEUE_SIZE    = 1000; // hard cap; older events drop on overflow

// In dev mode the queue is logged but not POSTed (no server yet).
// The Vite build will inject this from import.meta.env.
const DEV_MODE = typeof window !== "undefined" && window.__DEV_MODE__ === true;
const AUDIT_ENDPOINT = "/api/audit"; // Replit server route, TBD

// ─── Identity ─────────────────────────────────────────────────────────────

// Replaced by Okta JWT 'sub' claim once auth is wired (see DEMO_ONLY_INVENTORY.md).
// Until then, recordAudit accepts an `actor` param from the caller (typically
// state.user.id) so the demo identity flows through one channel.
function defaultActor() {
  if (typeof window !== "undefined" && window.__currentUser__) {
    return window.__currentUser__.oktaSub || "demo";
  }
  return "demo";
}

// ─── Queue ────────────────────────────────────────────────────────────────

let queue = loadQueue();
let flushTimer = null;
let bannerCallback = null; // set by registerBannerHandler
let flushInFlight = false;

function loadQueue() {
  try {
    const raw = localStorage.getItem(QUEUE_STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch (err) {
    log.error("auditLog: failed to load queue", { err: String(err) });
    return [];
  }
}

function persistQueue() {
  try {
    localStorage.setItem(QUEUE_STORAGE_KEY, JSON.stringify(queue));
  } catch (err) {
    // Quota exceeded. Drop the oldest 25% to make room and try again.
    if (queue.length > 100) {
      queue = queue.slice(Math.floor(queue.length * 0.25));
      try { localStorage.setItem(QUEUE_STORAGE_KEY, JSON.stringify(queue)); }
      catch { /* give up; in-memory queue still has the data for this session */ }
    }
    log.error("auditLog: queue persist failed", { err: String(err), queueSize: queue.length });
  }
}

// ─── Public API ───────────────────────────────────────────────────────────

/**
 * Record an audit event. Called by the API client after every successful write.
 *
 * @param {object}  event
 * @param {string}  event.action     e.g. "location.update", "lease.create", "watch_item.resolve"
 * @param {string}  event.entityType "location" | "lease" | "candidate" | "project" | "watch_item" | "allowance" | "draw" | "commentary"
 * @param {string}  event.entityId   PK of the affected row ("AMER-SFO-01", "L.AMER-SFO-01.2", uuid)
 * @param {object?} event.before     Snapshot pre-change. null for inserts.
 * @param {object?} event.after      Snapshot post-change. null for deletes.
 * @param {string?} event.actor      Okta sub (defaults to current user)
 */
export function recordAudit({ action, entityType, entityId, before = null, after = null, actor }) {
  if (!action || !entityType || !entityId) {
    log.error("auditLog: invalid event, missing required fields", { action, entityType, entityId });
    return;
  }

  const event = {
    action,
    entityType,
    entityId,
    before,
    after,
    occurredAt:  new Date().toISOString(),
    actorOktaSub: actor || defaultActor(),
    clientId:    crypto.randomUUID ? crypto.randomUUID() : `evt_${Date.now()}_${Math.random()}`,
  };

  queue.push(event);
  if (queue.length > MAX_QUEUE_SIZE) {
    queue = queue.slice(queue.length - MAX_QUEUE_SIZE);
  }
  persistQueue();
  log.audit(action, { entityType, entityId, actor: event.actorOktaSub });

  scheduleFlush();
}

/**
 * Force-flush the queue. Called on page unload and from admin tooling.
 */
export async function flushAuditQueue() {
  if (flushInFlight || queue.length === 0) return;
  if (DEV_MODE) {
    // Dev mode: log and clear, no server.
    log.audit("flush (dev)", { count: queue.length });
    queue = [];
    persistQueue();
    return;
  }

  flushInFlight = true;
  const batch = queue.slice(0, MAX_BATCH_SIZE);
  try {
    const res = await fetch(AUDIT_ENDPOINT, {
      method:  "POST",
      headers: { "Content-Type": "application/json" },
      body:    JSON.stringify({ events: batch }),
      credentials: "include",
    });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);

    // Drop the events we just sent (match by clientId).
    const sentIds = new Set(batch.map((e) => e.clientId));
    queue = queue.filter((e) => !sentIds.has(e.clientId));
    persistQueue();
    if (bannerCallback) bannerCallback({ type: "audit_ok", queueSize: queue.length });
  } catch (err) {
    log.error("auditLog: flush failed", { err: String(err), queueSize: queue.length });
    if (bannerCallback) bannerCallback({ type: "audit_failed", queueSize: queue.length, err: String(err) });
  } finally {
    flushInFlight = false;
  }
}

/**
 * Register a callback that the AppShell can use to surface banners.
 * Called once at app boot.
 *
 * @param {(payload: { type: 'audit_ok' | 'audit_failed', queueSize: number, err?: string }) => void} fn
 */
export function registerBannerHandler(fn) {
  bannerCallback = fn;
}

/**
 * Read-only view of the current queue. For admin debugging only.
 */
export function getPendingAudits() {
  return [...queue];
}

// ─── Scheduling ───────────────────────────────────────────────────────────

function scheduleFlush() {
  if (flushTimer) return;
  flushTimer = setTimeout(() => {
    flushTimer = null;
    flushAuditQueue();
  }, FLUSH_INTERVAL_MS);
}

// ─── Lifecycle hooks ──────────────────────────────────────────────────────

if (typeof window !== "undefined") {
  // Best-effort flush on tab close. `beforeunload` is sync, so we
  // can't await; the persistQueue call already wrote the queue to
  // localStorage on every recordAudit, so nothing is lost - the next
  // session picks up where this one left off.
  window.addEventListener("beforeunload", () => {
    if (queue.length > 0 && navigator.sendBeacon && !DEV_MODE) {
      const blob = new Blob([JSON.stringify({ events: queue })], { type: "application/json" });
      navigator.sendBeacon(AUDIT_ENDPOINT, blob);
      // Don't clear the queue here - if sendBeacon failed, next session retries.
      // Server is idempotent on (clientId).
    }
  });
}
